import { Link } from "react-router-dom";
import type { DateTime } from "luxon";
import { Copy01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { handleCopy } from "utils/handleCopy";
import type {
  AccessCode,
  AccessCodeShowing,
  LockboxCodeType
} from "api/landlord/lockboxes";
import {
  formatCodeWindow,
  formatShowingWindow,
  isCodeActive
} from "./codeScheduling";
import { LOCKBOX_CODE_TYPE_LABELS } from "./constants";

const CODE_TYPE_BADGE_VARIANTS: Record<LockboxCodeType, BadgeVariant> = {
  access: BadgeVariant.BASE,
  shackle: BadgeVariant.WARNING
};

const LeadName = ({ lead }: { lead: AccessCodeShowing["lead"] }) =>
  lead ? (
    <Link
      to={`/leads/${lead.id}`}
      className="font-medium text-primary-600 hover:underline"
    >
      {lead.name}
    </Link>
  ) : (
    <span>the lead</span>
  );

const ShowingLine = ({
  showing,
  zone,
  now
}: {
  showing: AccessCodeShowing;
  zone: string;
  now: DateTime;
}) => (
  <p className="text-sm text-content-500">
    For <LeadName lead={showing.lead} />
    &apos;s self-guided showing, {formatShowingWindow(
      showing,
      zone,
      now
    )} ·{" "}
    <Link
      to={`/showings?id=${showing.id}`}
      className="font-medium text-primary-600 hover:underline"
    >
      View showing
    </Link>
  </p>
);

// "Sat, Oct 17, 10:00 AM – 12:00 PM CDT · Smith Realty": the recipient shares the window's line.
const codeCaption = (code: AccessCode, zone: string, now: DateTime) => {
  const windowText = formatCodeWindow(code, zone, now);
  return code.recipient_name
    ? `${windowText} · ${code.recipient_name}`
    : windowText;
};

export const LockboxCodeRow = ({
  code,
  zone,
  now,
  actionLabels,
  onCancel
}: {
  code: AccessCode;
  zone: string;
  now: DateTime;
  actionLabels: "full" | "icon-copy";
  onCancel: (code: AccessCode) => void;
}) => {
  const active = isCodeActive(code, now);
  const canCancel = code.cancelable && code.source === "on_demand";
  const copyCode = () => handleCopy(code.code);
  const codeType = code.code_type ?? "access";

  return (
    <div className="flex items-center justify-between gap-2.5 py-4 first:pt-0">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-2xl font-bold text-content-1000">{code.code}</p>
          <Show if={code.vendor === "sentrilock"}>
            <Badge
              size={BadgeSize.SM}
              variant={CODE_TYPE_BADGE_VARIANTS[codeType]}
            >
              {LOCKBOX_CODE_TYPE_LABELS[codeType]}
            </Badge>
          </Show>
          <Badge
            size={BadgeSize.SM}
            variant={active ? BadgeVariant.SUCCESS : BadgeVariant.BASE}
          >
            {active ? "Active now" : "Scheduled"}
          </Badge>
        </div>
        <p className="text-sm font-medium text-content-400">
          {codeCaption(code, zone, now)}
        </p>
        {code.showing ? (
          <ShowingLine showing={code.showing} zone={zone} now={now} />
        ) : null}
      </div>
      <div className="flex shrink-0 gap-2">
        <Show if={canCancel}>
          <Button size={ButtonSize.SM} onClick={() => onCancel(code)}>
            Cancel code
          </Button>
        </Show>
        {actionLabels === "icon-copy" ? (
          <IconButton
            size={IconButtonSize.SM}
            variant={IconButtonVariant.BASE}
            HugeIcon={Copy01Icon}
            aria-label="Copy code"
            tooltip="Copy code"
            onClick={copyCode}
          />
        ) : (
          <Button size={ButtonSize.SM} HugeIcon={Copy01Icon} onClick={copyCode}>
            Copy code
          </Button>
        )}
      </div>
    </div>
  );
};
